import { ElementPlaceholder } from './ElementPlaceholder';
import { useElementBox, type ElementComponent } from './elementRenderContext';
import { textProp } from './elementProps';
import { sanitizeElementHtml } from './htmlSanitize';

// The three elements that are not content. Two of them are shapes; the third is the escape hatch, and
// it is the only place in this folder where markup a merchant wrote reaches the DOM.

// Draws a gap. Its height is a style, so with no styles it is a gap of nothing, which is honest.
export const SpacerElement: ElementComponent = ({ node }) => (
  <div {...useElementBox(node)} aria-hidden="true" />
);

/** The two halves of a labelled rule, drawn inside the box because the box is now a row, not a line. */
const RULE_HALF = { flexGrow: 1, flexBasis: 0, height: '1px', backgroundColor: 'currentColor', opacity: 0.35 };

/** What a labelled divider is instead of a painted 1px box: a row, whose own line the halves above draw. */
const LABELLED_ROW = { display: 'flex', alignItems: 'center', gap: '12px', height: 'auto', backgroundColor: 'transparent' };

/**
 * Draws a rule between two things — an `<hr>`, so it means something read aloud, and a labelled row
 * when the merchant wrote the word that goes in the middle of it. A word cannot sit inside an `<hr>`
 * and cannot sit inside a box one pixel tall, so the labelled shape is a different element, not the
 * same one with text on it: it overrides only the height and the ground its bare twin is painted with.
 */
export const DividerElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const label = textProp(node.props, 'label');
  if (label === '') return <hr {...box} />;
  return (
    <div {...box} role="separator" aria-label={label} style={LABELLED_ROW}>
      <span style={RULE_HALF} aria-hidden="true" />
      <span dir="auto">{label}</span>
      <span style={RULE_HALF} aria-hidden="true" />
    </div>
  );
};

/**
 * Draws the markup a merchant pasted, after the sanitiser has rewritten it as the subset of itself
 * this storefront allows. The settings grammar refused `<script`, `javascript:`, `data:text/html` and
 * `on*=` when they saved; it has nothing to say about `<iframe srcdoc>`, `<object>`, `<embed>` or
 * `<svg onbegin>`, so `sanitizeElementHtml` is what those four actually stop at.
 */
export const CustomHtmlElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const html = sanitizeElementHtml(node.props.html);
  // The builder names the empty box so it can be found and selected; a shopper still sees nothing.
  if (html === '') {
    return (
      <div {...box} data-empty="custom-html">
        <ElementPlaceholder label="Custom HTML" hint="Paste your markup in Settings." />
      </div>
    );
  }
  return <div {...box} dangerouslySetInnerHTML={{ __html: html }} />;
};
